import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { BORDER_RADIUS, VIDEO_FORMATS } from "../../../../utils/constants";
import {
  getMaxModalLayerZIndex,
  MODAL_LAYER_STEP,
} from "../../../../utils/modalLayer";
import { formatUrlMedia, getExtensionFile } from "../../../../utils/string";

const TOAST_TRANSITION_MS = 450;

const TOAST_MEDIA_SX = {
  width: 44,
  height: 44,
  flexShrink: 0,
  objectFit: "cover",
  borderRadius: 1,
  display: "block",
  bgcolor: "grey.100",
};

const resolveToastMediaType = ({ mediaType, url }) => {
  if (mediaType === "video") return "video";
  if (mediaType === "image") return "image";
  if (!url) return "image";
  if (url.startsWith("data:video/")) return "video";
  return VIDEO_FORMATS.includes(getExtensionFile(url)) ? "video" : "image";
};

const ToastMediaPlaceholder = () => <Box sx={TOAST_MEDIA_SX} />;

const CompetitorFavoriteToastMedia = ({
  mediaUrl = "",
  mediaType = "image",
}) => {
  const videoRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  const formattedUrl = formatUrlMedia(mediaUrl);
  const resolvedType = resolveToastMediaType({
    mediaType,
    url: formattedUrl,
  });

  useEffect(() => {
    setHasError(false);
  }, [formattedUrl, mediaType]);

  useEffect(() => {
    if (resolvedType !== "video") return undefined;

    const video = videoRef.current;
    if (!video) return undefined;

    const playVideo = () => {
      video.play().catch(() => setHasError(true));
    };

    if (video.readyState >= 2) {
      playVideo();
      return undefined;
    }

    video.addEventListener("loadeddata", playVideo, { once: true });

    return () => {
      video.removeEventListener("loadeddata", playVideo);
    };
  }, [formattedUrl, resolvedType]);

  if (!formattedUrl || hasError) {
    return <ToastMediaPlaceholder />;
  }

  if (resolvedType === "video") {
    return (
      <Box
        component="video"
        ref={videoRef}
        src={formattedUrl}
        muted
        playsInline
        autoPlay
        loop
        preload="auto"
        onError={() => setHasError(true)}
        sx={TOAST_MEDIA_SX}
      />
    );
  }

  return (
    <Box
      component="img"
      src={formattedUrl}
      alt=""
      referrerPolicy="no-referrer"
      onError={() => setHasError(true)}
      sx={TOAST_MEDIA_SX}
    />
  );
};

const CompetitorFavoriteToast = ({
  open = false,
  mediaUrl = "",
  mediaType = "image",
  message = "",
  toastKey = 0,
  showChangeLocation = false,
  changeLocationLabel = "",
  onChangeLocation = () => {},
  onDismiss = () => {},
  onExited = () => {},
}) => {
  const theme = useTheme();
  const cardRef = useRef(null);
  const toastZIndex =
    Math.max(getMaxModalLayerZIndex(), theme.zIndex.snackbar) +
    MODAL_LAYER_STEP;

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (cardRef.current?.contains(event.target)) return;
      onDismiss();
    };

    document.addEventListener("mousedown", onPointerDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [onDismiss, open]);

  if (!message || typeof document === "undefined") return null;

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        left: 24,
        zIndex: toastZIndex,
        pointerEvents: "none",
      }}
    >
      <Slide
        in={open}
        direction="up"
        timeout={{
          enter: TOAST_TRANSITION_MS,
          exit: TOAST_TRANSITION_MS - 100,
        }}
        easing={{
          enter: "cubic-bezier(0, 0, 0.2, 1)",
          exit: "cubic-bezier(0.4, 0, 1, 1)",
        }}
        onExited={onExited}
        mountOnEnter
        unmountOnExit
      >
        <Box
          ref={cardRef}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "nowrap",
            p: 1.5,
            minWidth: 280,
            maxWidth: 480,
            width: "100%",
            bgcolor: "fields.background",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            boxShadow: BOX_SHADOW_THEME,
            borderRadius: BORDER_RADIUS,
            pointerEvents: "auto",
            transition: "opacity 0.25s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "nowrap",
              minWidth: 0,
              flex: 1,
            }}
          >
            <CompetitorFavoriteToastMedia
              key={toastKey}
              mediaUrl={mediaUrl}
              mediaType={mediaType}
            />
            <Typography
              variant="body2"
              component="span"
              noWrap
              sx={{
                color: "text.primary",
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {message}
            </Typography>
          </Box>
          {showChangeLocation ? (
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={onChangeLocation}
              sx={{
                color: "text.primary",
                fontWeight: 500,
                fontSize: 14,
                whiteSpace: "nowrap",
                flexShrink: 0,
                ml: 3,
                mt: 0.05,
              }}
            >
              {changeLocationLabel}
            </Link>
          ) : null}
        </Box>
      </Slide>
    </Box>,
    document.body
  );
};

export default CompetitorFavoriteToast;
